import React from 'react';

import Flame from '@/assets/icons/Flame.svg';
import { dateRangeMedWithZoneOffset } from '../../helpers/dates';
import { IContentfulImage } from '../../interfaces/contentful-image.interface';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import { FavouriteButton } from '../ui/favourite-button/favourite-button';
import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import LinkWrapper from '../link-wrapper/link-wrapper';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import { CountdownState } from '@/helpers/countdown';
import { getTagBorderColor } from '@/helpers/tailwindColors';

interface OfferCardProps {
  offer: IMeetingPlacePageOfferItem;
  node_locale: string;
  logo?: IContentfulImage;
  header?: string;
  parentSlug?: string;
  countdownState?: CountdownState;
}

export const OfferCard = ({
  offer,
  node_locale,
  logo,
  header,
  parentSlug,
  countdownState = { type: 'none' },
}: OfferCardProps) => {
  const dictionary = useGetLibraryConstantsDictionaryData();
  const config = useTenantConfig();
  const isLivliFamily =
    config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true' &&
    offer?.typeOfOffer?.internalName === 'specialOffer';

  const aspectRatio =
    logo?.width && logo?.height
      ? parseInt(logo.width) / parseInt(logo.height)
      : undefined;

  const isNearlySquare =
    typeof aspectRatio === 'number' && Math.abs(aspectRatio - 1) < 0.1;
  const typeOfOfferClass =
    offer?.typeOfOffer?.internalName === 'specialOffer'
      ? 'bg-brand-yellow-1'
      : offer?.typeOfOffer?.internalName === 'ikeaFamilyMemberOffer'
        ? 'bg-brand-blue-4 text-text-inverse'
        : 'bg-brand-grey-1';

  const renderDateOrCountdown = () => {
    switch (countdownState.type) {
      case 'minutely': {
        const minuteText =
          countdownState.minutes === 1
            ? dictionary?.minuteLeft
            : dictionary?.minutesLeft;
        return (
          <div className="flex items-center text-bodys sm:text-bodyl font-bold text-text-default">
            <Flame className="h-6 w-6 lg:h-8 lg:w-8" viewBox="0 0 32 32" />
            {minuteText.replace('__', countdownState.minutes?.toString())}
          </div>
        );
      }
      case 'hourly': {
        const hourText =
          countdownState.hours === 1
            ? dictionary?.hourLeft
            : dictionary?.hoursLeft;
        return (
          <div className="flex items-center gap-1 text-bodys sm:text-bodyl font-bold text-text-default">
            <Flame className="h-6 w-6 lg:h-8 lg:w-8" viewBox="0 0 32 32" />
            {hourText.replace('__', countdownState.hours?.toString())}
          </div>
        );
      }
      default:
        return (
          <div className="text-bodys sm:text-bodyl text-text-default">
            {dateRangeMedWithZoneOffset(
              offer?.validFrom.toString(),
              offer.validTo.toString(),
              node_locale,
            )}
          </div>
        );
    }
  };
  return (
    <div className="relative w-full flex flex-col">
      <LinkWrapper
        data={{
          // customizedSlug: offer.customizedSlug,
          name: offer.name,
          sys: { locale: node_locale },
          slug: offer.slug,
          urlSubDirectory: parentSlug,
        }}
        className={`group flex flex-col h-full flex-shrink-0 w-[224px] rounded-xl sm:w-full overflow-hidden bg-background-primary-light transition-opacity duration-200 ease-in-out lg:hover:bg-background-secondary focus:outline focus:outline-3 focus:outline-offset-3 focus:outline-text-light `}
      >
        {logo && (
          <ImageWrapper
            url={logo.url}
            alt={logo?.description ? logo?.description : ''}
            className={`mt-3 ml-3 sm:mt-4 sm:ml-4 block h-12 w-12 md:h-20 md:w-20 rounded-[12px] sm:rounded-lg bg-brand-white flex items-center justify-center ${!isNearlySquare ? 'border border-brand-grey-2' : ''}`}
            imgClassName={
              isNearlySquare
                ? '!object-contain rounded-[12px] sm:rounded-lg border border-brand-grey-2'
                : '!object-contain'
            }
            width={logo?.width ? parseInt(logo.width) : undefined}
            height={logo?.height ? parseInt(logo.height) : undefined}
          />
        )}
        <div className="mt-3 sm:mt-6 flex-1 flex flex-col">
          <div className="sm:min-h-[170px] px-5 sm:px-6">
            {header && (
              <div className="pb-1 text-bodys sm:text-bodyl text-text-default">
                {header}
              </div>
            )}
            {offer && offer?.name && (
              <h2 className="pb-1 font-title text-headings md:text-headingm text-text-default break-words">
                {offer.name}
              </h2>
            )}
            <div className="text-bodys sm:text-bodyl text-text-default">
              {renderDateOrCountdown()}
            </div>
            {offer &&
              offer?.offerCampaignTagsCollection?.items &&
              offer?.offerCampaignTagsCollection?.items?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {offer?.offerCampaignTagsCollection?.items?.map(
                    (tag, index) => {
                      const borderColor = getTagBorderColor(tag?.colour);
                      return (
                        <span
                          key={index}
                          className={`border-2 ${borderColor} md:py-2 px-2 md:px-3 rounded-sm md:rounded-md text-bodyxs font-bold md:text-headingxxs text-neutral-grey-700 whitespace-nowrap`}
                        >
                          {tag?.tagName}
                        </span>
                      );
                    },
                  )}
                </div>
              )}
          </div>

          {/* Spacer — absorbs all extra space */}
          <div className="flex-1" />
          {/* Ticket tear divider — sits at the seam between primary and yellow */}
          <div className="relative flex items-center mt-7">
            {/* Left punch-hole */}
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-brand-white z-10" />
            {/* Right punch-hole */}
            <div className="absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-brand-white z-10" />
          </div>

          <div
            className={`flex items-center justify-center text-text-default ${typeOfOfferClass} relative`}
          >
            <div className="absolute top-0 left-0 right-0 border-t-4 border-dotted -translate-y-1/2 border-brand-white" />
            <div
              className={`font-title text-headings md:text-headingm text-center py-6 md:py-8`}
            >
              {isLivliFamily
                ? dictionary?.livliFamily
                : offer.typeOfOffer?.name}
            </div>
          </div>
        </div>
      </LinkWrapper>
      <FavouriteButton
        id={offer.sys?.id ?? ''}
        type="offer"
        className="absolute top-3 right-3 md:top-4 md:right-4 z-1 md:h-16 md:w-16"
        iconclass="md:h-[32px] md:w-[34px]"
        name={offer?.name}
      />
    </div>
  );
};

export default OfferCard;
